/* ============================================
   THE PRINT LOFT — Frame Picker (hybrid)
   Desktop: text list (iteration B). Mobile: dropdown (iteration A) with
   price line above the select.

   - Disables the off-viewport controller so only one input posts to /cart/add
   - Syncs radio ↔ select on user change
   - On Size/Finish change, recomputes per-frame deltas from the JSON map
     baked into [data-frame-prices] at server render time
   - Prices live inside <span class="money"> so Bucks converts cleanly. After
     each price update we ask Bucks to reConvert.

   Pure helpers (formatPaise, priceKey, lookupPrice, computeRowState,
   computeMobileLabel, getCheckedRadioValue) are mirrored verbatim in
   /tests/helpers/frame-picker.js — keep them in lockstep.
============================================ */

(function () {
  'use strict';

  // ─── International framing restriction ───────────────────────────────────
  // Some sizes ship unframed-only outside India (large framed glass = breakage/
  // cost liability). The picker root carries data-intl-frame-restrict="SizeA|SizeB"
  // listing the restrictable sizes. Whether the restriction APPLIES is decided
  // client-side by isInternationalNow() — because this store prices in INR and the
  // Bucks currency converter (NOT Shopify's localization.country) reflects the
  // buyer's real market. Shopify's country defaults to IN for most visitors, so
  // gating on it silently missed international buyers being shown € / $ prices.
  var INTL_RESTRICT_MSG = 'Not available for international shipping';
  var INTL_RESTRICT_LABEL = 'Sold out';

  // True when the buyer is NOT a domestic (India/INR) order. Restrict-if-either:
  // returns true as soon as ANY signal reads international, so a single missed
  // signal can never let a big framed piece ship abroad. Signals, in order:
  //   1) Bucks currency in localStorage (set early + persisted across visits)
  //   2) Shopify Markets country
  //   3) rendered money symbol (after Bucks converts the DOM)
  // Defaults to false (domestic) when nothing is readable, so a total signal
  // failure never hides frames from Indian buyers (the primary market).
  function isInternationalNow() {
    try {
      var keys = ['buckscc_currency', 'buckscc_selected_currency', 'buckscc_auto_currency'];
      for (var i = 0; i < keys.length; i++) {
        var raw = window.localStorage && localStorage.getItem(keys[i]);
        if (!raw) continue;
        var cur = null;
        try {
          var v = JSON.parse(raw);
          cur = (v && typeof v === 'object' && (v.currency || v.code)) || (typeof v === 'string' ? v : null);
        } catch (_) { cur = raw; }
        if (cur && String(cur).toUpperCase().indexOf('INR') === -1) return true;
      }
    } catch (_) {}
    try {
      var c = (window.Shopify && window.Shopify.country) || '';
      if (c && String(c).toUpperCase() !== 'IN') return true;
    } catch (_) {}
    try {
      var m = document.querySelector('.tpl-frame-row__price .money, .price .money, .money');
      if (m && m.textContent && /[$€£¥₩]/.test(m.textContent) && !/₹/.test(m.textContent)) return true;
    } catch (_) {}
    return false;
  }

  // ─── Pure helpers (mirrored in /tests/helpers/frame-picker.js) ───────────

  function formatPaise(paise) {
    if (typeof paise !== 'number' || !isFinite(paise)) return '₹0';
    var rupees = Math.round(paise / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function priceKey(size, finish, frame) {
    return String(size) + '|' + String(finish) + '|' + String(frame);
  }

  function lookupPrice(prices, size, finish, frame) {
    if (!prices || typeof prices !== 'object') return null;
    var entry = prices[priceKey(size, finish, frame)];
    if (entry && typeof entry.p === 'number') return entry;
    // Fallback: products without a Finish option store keys with an empty finish
    // segment ("Medium||Gallery White"). If the explicit-finish lookup misses,
    // try with empty finish so we still find the variant.
    if (finish !== '') {
      entry = prices[priceKey(size, '', frame)];
      if (entry && typeof entry.p === 'number') return entry;
    }
    return null;
  }

  function computeRowState(prices, size, finish, frame) {
    var baseEntry = lookupPrice(prices, size, finish, 'Unframed');
    var entry = lookupPrice(prices, size, finish, frame);
    if (!entry || !entry.a) {
      return { available: false, label: 'unavailable', delta: 0, kind: 'unavailable' };
    }
    var base = baseEntry ? baseEntry.p : entry.p;
    var delta = entry.p - base;
    if (delta > 0) {
      return { available: true, label: '+ ' + formatPaise(delta), delta: delta, kind: 'paid' };
    }
    return { available: true, label: 'incl.', delta: 0, kind: 'incl' };
  }

  function computeMobileLabel(prices, size, finish, selectedFrame) {
    if (!selectedFrame) return '—';
    var state = computeRowState(prices, size, finish, selectedFrame);
    if (state.kind === 'unavailable') return '—';
    return state.label;
  }

  function getCheckedRadioValue(radios) {
    if (!radios) return '';
    for (var i = 0; i < radios.length; i++) {
      if (radios[i] && radios[i].checked) return radios[i].value;
    }
    return '';
  }

  // ─── DOM-bound init ───────────────────────────────────────────────────

  var MQ = window.matchMedia('(max-width: 749px)');

  // Module-level registry of initialised pickers so global variant:change
  // listeners can fan out to every instance without re-binding per init.
  var REGISTRY = [];
  var GLOBAL_LISTENERS_BOUND = false;

  function bindGlobalVariantListenersOnce() {
    if (GLOBAL_LISTENERS_BOUND) return;
    GLOBAL_LISTENERS_BOUND = true;
    function fanOut() {
      // Filter out detached pickers each time, no full GC needed
      REGISTRY = REGISTRY.filter(function (p) {
        return p.root && p.root.isConnected;
      });
      REGISTRY.forEach(function (p) {
        try { setTimeout(p.updateAllRowPrices, 0); } catch (_) {}
      });
    }
    ['variant:change', 'variantChange', 'on:variant:change'].forEach(function (evt) {
      document.addEventListener(evt, fanOut);
    });
  }

  function getCurrentSizeFinish(root) {
    var ctx = root.closest('product-info, [data-product-info], section, form') || document;
    // B-3: pickers now standardize on name="options[X]" everywhere.
    function readOption(name) {
      var sel = 'options[' + name + ']';
      var radio = ctx.querySelector('input[name="' + sel + '"]:checked');
      if (radio) return radio.value;
      var select = ctx.querySelector('select[name="' + sel + '"]');
      if (select) return select.value;
      return '';
    }
    return { size: readOption('Size'), finish: readOption('Finish') };
  }

  // Which option changes make this picker recompute. Size and Finish move the
  // prices. Mat (Architectural Mat products, 2026-10-03) moves neither, but it
  // IS a variant change, so Dawn rewrites every option label to its raw value
  // and this recompute is what writes the display label ("Panel only") back.
  // Leave Mat out and the label stays wrong until something else moves.
  function isSizeOrFinishName(name) {
    return (
      name === 'options[Size]' ||
      name === 'options[Finish]' ||
      name === 'options[Mat]'
    );
  }

  function bucksReconvert() {
    try {
      if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
        window.bucksCC.reConvert();
      }
    } catch (_) {}
  }

  function renderPriceWrap(priceWrap, state) {
    if (!priceWrap) return;
    priceWrap.classList.remove('tpl-frame-row__price--incl');
    priceWrap.classList.remove('tpl-frame-row__price--unavailable');
    priceWrap.classList.remove('tpl-frame-row__price--intl');
    if (state.kind === 'paid') {
      priceWrap.innerHTML = '+&nbsp;<span class="money">' + formatPaise(state.delta) + '</span>';
    } else if (state.kind === 'incl') {
      priceWrap.classList.add('tpl-frame-row__price--incl');
      priceWrap.textContent = 'incl.';
    } else {
      priceWrap.classList.add('tpl-frame-row__price--unavailable');
      priceWrap.textContent = 'unavailable';
    }
  }

  function renderRestrictedPriceWrap(priceWrap) {
    if (!priceWrap) return;
    priceWrap.classList.remove('tpl-frame-row__price--incl');
    priceWrap.classList.add('tpl-frame-row__price--unavailable');
    priceWrap.classList.add('tpl-frame-row__price--intl');
    priceWrap.textContent = INTL_RESTRICT_LABEL;
  }

  function renderMobilePriceLine(el, state) {
    if (!el) return;
    if (state.kind === 'paid') {
      el.classList.remove('tpl-frame-picker__price-value--incl');
      el.innerHTML = '+&nbsp;<span class="money">' + formatPaise(state.delta) + '</span>';
    } else if (state.kind === 'incl') {
      el.classList.add('tpl-frame-picker__price-value--incl');
      el.textContent = 'Incl. in price';
    } else {
      el.classList.remove('tpl-frame-picker__price-value--incl');
      el.textContent = '';
    }
  }

  function init(root) {
    if (!root || root._tplFrameInit) return;
    root._tplFrameInit = true;
    // Mark in-DOM so the MutationObserver's pending query short-circuits
    root.setAttribute('data-tpl-frame-init-done', '');

    var prices = {};
    try { prices = JSON.parse(root.getAttribute('data-frame-prices') || '{}'); } catch (_) {}

    var radios = root.querySelectorAll('[data-frame-radio]');
    var select = root.querySelector('[data-frame-select]');
    var rows = root.querySelectorAll('[data-frame-row]');
    var priceLineEl = root.querySelector('[data-frame-current-price]');

    // Sizes whose paid frames CAN be blocked internationally (always emitted;
    // whether the block APPLIES is decided by intlNow() below).
    var intlRestrict = (root.getAttribute('data-intl-frame-restrict') || '')
      .split('|')
      .filter(function (s) { return s.length > 0; });

    // Server verdict from localization.country (sticky). When the server already
    // rendered this buyer as international it painted the paid rows blocked — the
    // client must NEVER loosen that, even if Bucks/Shopify signals haven't loaded
    // yet at t=0 (which would otherwise re-enable a correctly-blocked frame).
    var serverIntl = root.getAttribute('data-intl-server') === '1';

    function isSizeRestricted(size) {
      return intlRestrict.length > 0 && intlRestrict.indexOf(size) !== -1;
    }

    // International = server said so (sticky) OR any live client signal says so.
    function intlNow() {
      return serverIntl || isInternationalNow();
    }

    // Move selection off a now-blocked paid frame back to Unframed. Dispatches a
    // change on the canonical select so Dawn re-matches the variant; the select's
    // own change handler syncs the radios + price line.
    function forceUnframed() {
      if (select && select.value !== 'Unframed') {
        select.value = 'Unframed';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function syncBreakpoint() {
      // The select is the canonical Framing input for Dawn's variant matcher
      // (Dawn queries `select, fieldset` and we want it to find ONE element for Framing).
      // Both controllers stay enabled across viewports; CSS hides the off-viewport one.
      // Radios stay enabled but follow per-row availability.
      for (var i = 0; i < radios.length; i++) {
        var row = radios[i].closest('[data-frame-row]');
        radios[i].disabled = !!(row && row.classList.contains('is-unavailable'));
      }
      // Never disable the select — Dawn must always be able to read its value.
      if (select) select.disabled = false;
    }

    function setSelectedAttribute(value) {
      for (var i = 0; i < rows.length; i++) {
        rows[i].setAttribute('data-selected', rows[i].getAttribute('data-frame-value') === value ? 'true' : 'false');
      }
    }

    function updateMobilePriceLine() {
      if (!priceLineEl) return;
      var sf = getCurrentSizeFinish(root);
      var selectedValue = (select && select.value) || getCheckedRadioValue(radios) || 'Unframed';
      var state = computeRowState(prices, sf.size, sf.finish, selectedValue);
      // Mobile picker should never select an unavailable; fall back gracefully.
      if (state.kind === 'unavailable') {
        renderMobilePriceLine(priceLineEl, { kind: 'unknown' });
        return;
      }
      renderMobilePriceLine(priceLineEl, state);
    }

    function updateAllRowPrices() {
      var sf = getCurrentSizeFinish(root);
      // Defensive: bail only when Size is unreadable. Finish can be empty when
      // the product doesn't have a Finish option (the JSON map stores empty-finish
      // keys for those, and lookupPrice falls back to them). Bailing on empty
      // finish would freeze the picker on the initial size's Liquid-rendered prices.
      if (!sf.size) return;

      var sizeRestricted = isSizeRestricted(sf.size) && intlNow();

      // If the buyer had a paid frame selected and just landed on a restricted
      // size, bounce the selection back to Unframed before rendering the rows.
      if (sizeRestricted) {
        var current = (select && select.value) || getCheckedRadioValue(radios);
        if (current && current !== 'Unframed') forceUnframed();
      }

      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var value = row.getAttribute('data-frame-value');
        var priceWrap = row.querySelector('.tpl-frame-row__price');
        var radio = row.querySelector('[data-frame-radio]');
        var restricted = sizeRestricted && value !== 'Unframed';

        if (restricted) {
          row.classList.add('is-unavailable');
          row.setAttribute('title', INTL_RESTRICT_MSG);
          if (radio) radio.disabled = true;
          renderRestrictedPriceWrap(priceWrap);
          continue;
        }

        row.removeAttribute('title');
        var state = computeRowState(prices, sf.size, sf.finish, value);
        if (!state.available) {
          row.classList.add('is-unavailable');
          if (radio) radio.disabled = true;
        } else {
          row.classList.remove('is-unavailable');
          if (radio) radio.disabled = false;
        }
        renderPriceWrap(priceWrap, state);
      }

      // Mobile select option labels
      if (select) {
        for (var j = 0; j < select.options.length; j++) {
          var opt = select.options[j];
          var optRestricted = sizeRestricted && opt.value !== 'Unframed';
          var entry = lookupPrice(prices, sf.size, sf.finish, opt.value);
          var available = !!(entry && entry.a);
          opt.disabled = optRestricted || !available;
          // data-label = display override from Liquid (e.g. "Panel only" on
          // Aluminium Architectural Mat products); value stays canonical.
          var baseLabel = opt.getAttribute('data-label') || opt.value;
          var label = baseLabel + (optRestricted ? ' · ' + INTL_RESTRICT_LABEL : (available ? '' : ' · unavailable'));
          if (opt.textContent.trim() !== label) opt.textContent = label;
        }
      }

      updateMobilePriceLine();
      bucksReconvert();
    }

    // Sync radio → select + selected attribute + mobile price line
    for (var ri = 0; ri < radios.length; ri++) {
      radios[ri].addEventListener('change', (function (r) {
        return function () {
          if (!r.checked) return;
          if (select && select.value !== r.value) select.value = r.value;
          setSelectedAttribute(r.value);
          updateMobilePriceLine();
          bucksReconvert();
        };
      })(radios[ri]));
    }

    // Sync select → radio + selected attribute + mobile price line
    if (select) {
      select.addEventListener('change', function () {
        for (var k = 0; k < radios.length; k++) {
          if (radios[k].value === select.value) { radios[k].checked = true; break; }
        }
        setSelectedAttribute(select.value);
        updateMobilePriceLine();
        bucksReconvert();
      });
    }

    // Listen for Size/Finish changes — defensive: bind to BOTH the closest
    // section AND document. Dawn may replace the section after a variant change,
    // which would orphan a section-bound listener; the document fallback always
    // catches the next change.
    var ctx = root.closest('product-info, [data-product-info], section') || document;
    function onOptionChange(e) {
      // B-6: dedupe — when section + document both fire for the same event,
      // only run once. Section fires first (earlier in bubbling), tags the
      // event; document handler bails on the tag.
      if (e._tplFramePickerSeen) return;
      e._tplFramePickerSeen = true;
      var t = e.target;
      if (!t || !t.name) return;
      if (!isSizeOrFinishName(t.name)) return;
      // Only act if this change targets a control in our same form/section,
      // so two PDPs on one page don't cross-update.
      var ownerForm = t.closest('form, product-info, [data-product-info], section');
      var rootCtx = root.closest('form, product-info, [data-product-info], section');
      if (ownerForm && rootCtx && ownerForm !== rootCtx) return;
      setTimeout(updateAllRowPrices, 0);
    }
    ctx.addEventListener('change', onOptionChange);
    if (ctx !== document) document.addEventListener('change', onOptionChange);

    // Viewport changes
    if (typeof MQ.addEventListener === 'function') {
      MQ.addEventListener('change', syncBreakpoint);
    } else if (typeof MQ.addListener === 'function') {
      MQ.addListener(syncBreakpoint);
    }

    syncBreakpoint();

    // Register this picker so module-level events can fan out to it
    REGISTRY.push({ root: root, updateAllRowPrices: updateAllRowPrices });
    bindGlobalVariantListenersOnce();

    // Force a price recompute on init so the picker always matches the
    // currently-selected size, even if Dawn re-rendered the surrounding section
    // without re-rendering the picker. This is the source of truth for prices —
    // the JSON map has every size×finish×frame combination baked in server-side,
    // so reading the current size from the DOM and looking up the entry is the
    // most reliable path. setTimeout(0) defers until any in-flight Dawn updates
    // settle.
    setTimeout(updateAllRowPrices, 0);

    // Bucks writes its detected currency to localStorage / converts the DOM
    // slightly after load, so on a first visit isInternationalNow() can read as
    // domestic at t=0. Re-check once the currency has settled — but ONLY when a
    // restrictable size is actually selected on load (e.g. a square-only product),
    // so the 90% domestic / non-restricted PDPs don't pay for two extra reconverts.
    if (isSizeRestricted(getCurrentSizeFinish(root).size)) {
      setTimeout(updateAllRowPrices, 600);
      setTimeout(updateAllRowPrices, 1600);
    }
  }

  function bootstrap() {
    var roots = document.querySelectorAll('[data-tpl-frame-picker]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Re-init after Dawn re-renders the section after a variant change
  var rerunObs = new MutationObserver(function () {
    var pending = document.querySelectorAll('[data-tpl-frame-picker]:not([data-tpl-frame-init-done])');
    if (pending.length) bootstrap();
  });
  if (document.body) rerunObs.observe(document.body, { childList: true, subtree: true });

  // Expose helpers for tests / debugging (no-op in non-test contexts)
  window.__tplFramePicker = {
    formatPaise: formatPaise,
    priceKey: priceKey,
    lookupPrice: lookupPrice,
    computeRowState: computeRowState,
    computeMobileLabel: computeMobileLabel,
    getCheckedRadioValue: getCheckedRadioValue
  };
})();
